import { useEffect, useRef, useState } from 'react';

const ITEM_GAP_PX = 8; // gap-2 = 0.5rem
const OVERFLOW_BADGE_WIDTH_PX = 40; // approximate width of "+N" badge

/**
 * Dynamically calculates how many items fit in a container before an overflow badge
 * is needed, based on actual rendered item widths and observed container width.
 *
 * Uses a hidden shadow clone (invisible absolute div) to measure item widths without
 * affecting layout. A ResizeObserver on the container recalculates on every resize.
 * Always shows at least 1 item regardless of container width.
 *
 * When not all items fit, the badge is always reserved on the right edge so the user
 * always knows there are more items (even when only 1 item is visible).
 * Pair with `pr-12` on the items area and an absolute-positioned badge — see cells.
 *
 * Usage:
 * ```tsx
 * const { containerRef, shadowRef, visibleCount } = useVisibleItemCount(items.length);
 * const hiddenCount = items.length - visibleCount;
 *
 * return (
 *   <div ref={containerRef} className="relative min-w-0">
 *     <div ref={shadowRef} aria-hidden="true" className="pointer-events-none invisible absolute flex gap-2">
 *       {items.map(item => <Badge key={item.id}>{item.name}</Badge>)}
 *     </div>
 *     <div className="flex items-center gap-2">
 *       <div className={cn('flex gap-2', hiddenCount > 0 && 'min-w-0 overflow-hidden')}>
 *         {items.slice(0, visibleCount).map(item => <Badge key={item.id}>{item.name}</Badge>)}
 *       </div>
 *       {hiddenCount > 0 && (
 *         <div className="shrink-0"><Badge>+{hiddenCount}</Badge></div>
 *       )}
 *     </div>
 *   </div>
 * );
 * ```
 */
export function useVisibleItemCount(itemCount: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(1);

  useEffect(() => {
    const container = containerRef.current;
    const shadow = shadowRef.current;
    if (!container || !shadow) return;

    const calculate = () => {
      const containerWidth = container.clientWidth;
      if (!containerWidth || shadow.children.length === 0) return;

      const children = Array.from(shadow.children) as HTMLElement[];

      // First check: do all items fit without a badge?
      const totalWidth = children.reduce(
        (sum, el, i) => sum + el.offsetWidth + (i > 0 ? ITEM_GAP_PX : 0),
        0
      );
      if (totalWidth <= containerWidth) {
        setVisibleCount(children.length);
        return;
      }

      // Not all fit — reserve badge space from the right and calculate how many items fit.
      // Badge width + gap is always subtracted so the badge is always visible.
      const availableWidth = containerWidth - ITEM_GAP_PX - OVERFLOW_BADGE_WIDTH_PX;
      let used = 0;
      let count = 0;

      for (let i = 0; i < children.length; i++) {
        const itemWidth = children[i].offsetWidth;

        if (count === 0) {
          // Always show at least the first item (pr-12 on items area clips it before the badge)
          used = itemWidth;
          count = 1;
          continue;
        }

        if (used + ITEM_GAP_PX + itemWidth <= availableWidth) {
          used += ITEM_GAP_PX + itemWidth;
          count++;
        } else {
          break;
        }
      }

      setVisibleCount(Math.max(1, count));
    };

    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    calculate();

    return () => observer.disconnect();
  }, [itemCount]);

  return { containerRef, shadowRef, visibleCount };
}
